import React, { useState, useRef, useEffect } from 'react';
import MediumButton from './MediumButton';
import SmallButton from './SmallButton';
import RemixSubmenu from './RemixSubmenu';
import Badge from './Badge';
import typography from '../../utils/typography';
import colors from '../../utils/colors';
import {
  ThumbsUpIcon,
  ThumbsDownIcon,
  MoreHorizontalIcon,
  ShareIcon,
  SlidersIcon,
  PlayIcon,
  PauseIcon,
  CommentIcon,
  TriangleDownIcon
} from '../../utils/icons';

// Helper function to format like counts
const formatLikeCount = (count) => {
  if (count >= 1000) {
    const thousands = count / 1000;
    // If it's a whole number (like 1000, 2000), show as "1k", "2k"
    if (thousands % 1 === 0) {
      return thousands + 'k';
    }
    // Otherwise show one decimal place (like 1.1k, 2.1k)
    return thousands.toFixed(1) + 'k';
  }
  return count.toString();
};

// Sound Wave Animation Component
const SoundWave = () => (
  <div className="flex items-center justify-center gap-1">
    <div className="w-1 bg-white rounded-full animate-pulse" style={{ 
      height: '16px',
      animation: 'soundWave1 1.2s ease-in-out infinite'
    }} />
    <div className="w-1 bg-white rounded-full animate-pulse" style={{ 
      height: '12px',
      animation: 'soundWave2 1.2s ease-in-out infinite'
    }} />
    <div className="w-1 bg-white rounded-full animate-pulse" style={{ 
      height: '20px',
      animation: 'soundWave3 1.2s ease-in-out infinite'
    }} />
    <div className="w-1 bg-white rounded-full animate-pulse" style={{ 
      height: '14px',
      animation: 'soundWave4 1.2s ease-in-out infinite'
    }} />
  </div>
);


const StatusIndicator = ({ status = "active" }) => {
  const statusColors = {
    active: "#FD429C",
    inactive: "#726E6C",
    pending: "#F59E0B"
  };

  return (
    <svg
      width={8}
      height={8}
      viewBox="0 0 8 8"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="flex-grow-0 flex-shrink-0"
    >
      <circle cx={4} cy={4} r={4} fill={statusColors[status]} />
    </svg>
  );
};

const Cell = ({ 
  title, 
  genres, 
  duration = "2:56", 
  image,
  status = "active",
  isNew = false,
  isLiked = false,
  isDisliked = false,
  isSelected = false,
  isPublished = false,
  isPlaying = false,
  multiSelectMode = false,
  isLibrary = false,
  likeCount = 0,
  commentCount = 230,
  isCommented = false,
  isCompact = false,
  onPublish,
  onRemix,
  onLike,
  onDislike,
  onShare,
  onComment,
  onMenuClick,
  onSelect,
  onPlay,
  onPause,
  onMarkAsPlayed,
  onRowClick,
  onCollapseNavigation,
  className = ""
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showRemixSubmenu, setShowRemixSubmenu] = useState(false);
  const [shouldShowThirdRow, setShouldShowThirdRow] = useState(false);
  const remixButtonRef = useRef(null);
  const submenuRef = useRef(null);
  const cellRef = useRef(null);


  // Handle click outside to close submenu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (submenuRef.current && !submenuRef.current.contains(event.target) &&
          remixButtonRef.current && !remixButtonRef.current.contains(event.target)) {
        setShowRemixSubmenu(false);
      }
    };

    if (showRemixSubmenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showRemixSubmenu]);

  // Handle responsive layout changes
  useEffect(() => {
    const checkWidth = () => {
      if (cellRef.current) {
        const cellWidth = cellRef.current.offsetWidth;
        const shouldShow = cellWidth < 600;
        setShouldShowThirdRow(shouldShow);
      }
    };

    // Initial check with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(checkWidth, 100);
    
    // ResizeObserver for cell changes
    let resizeObserver;
    if (cellRef.current) {
      resizeObserver = new ResizeObserver(checkWidth);
      resizeObserver.observe(cellRef.current);
    }

    // Window resize as fallback
    const handleWindowResize = () => {
      setTimeout(checkWidth, 50);
    };
    
    window.addEventListener('resize', handleWindowResize);

    return () => {
      clearTimeout(timeoutId);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', handleWindowResize);
    };
  }, []);

  const handleRemixClick = (e) => {
    e.stopPropagation(); // Always stop propagation to prevent row click
    setShowRemixSubmenu(!showRemixSubmenu);
  };

  const handleRemixAction = (action) => {
    console.log(`Remix action: ${action}`);
    // Close the submenu and remove hover state
    setShowRemixSubmenu(false);
    setIsHovered(false);
    
    // Collapse navigation when any remix option is selected
    if (onCollapseNavigation) {
      onCollapseNavigation();
    }
    
    // For cover action, we need to call a different handler
    if (action === 'cover') {
      // This will trigger the cover flow - the action will be passed up through onRemix
      onRemix?.(action);
    } else {
      onRemix?.(action);
    }
  };

  const handleLikeClick = () => {
    onLike?.();
  };

  const handleDislikeClick = () => {
    onDislike?.();
  };

  const handleRowClick = (e) => {
    if (multiSelectMode) {
      onSelect?.();
    } else if (e.metaKey || e.ctrlKey) {
      // Command/Ctrl + click enters multi-select mode and selects this item
      onSelect?.();
    } else {
      // Normal click - trigger row click callback
      onRowClick?.();
    }
  };

  const handlePlayClick = () => {
    onPlay?.();
    // Mark song as no longer new when played
    if (isNew && onMarkAsPlayed) {
      onMarkAsPlayed();
    }
  };

  const handlePauseClick = () => {
    onPause?.();
  };

  const handleDoubleClick = () => {
    if (!multiSelectMode) {
      handlePlayClick();
    }
  };

  // Wrapper functions to stop event propagation when in multi-select mode
  const handleActionClick = (callback) => (e) => {
    if (multiSelectMode) {
      e.stopPropagation();
    }
    callback?.();
  };

  return (
    <>
      {/* CSS animations for sound waves */}
      <style>
        {`
          @keyframes soundWave1 {
            0%, 40%, 100% { height: 16px; }
            20% { height: 8px; }
          }
          @keyframes soundWave2 {
            0%, 20%, 100% { height: 12px; }
            10% { height: 20px; }
          }
          @keyframes soundWave3 {
            0%, 80%, 100% { height: 20px; }
            40% { height: 8px; }
          }
          @keyframes soundWave4 {
            0%, 60%, 100% { height: 14px; }
            30% { height: 18px; }
          }
        `}
      </style>
      
      <div 
        ref={cellRef}
        className={`cell flex ${isCompact ? 'justify-between' : 'items-center'} w-full min-w-0 ${isCompact ? 'h-[76px]' : 'h-[110px]'} relative ${isCompact ? 'pl-6 pr-6 py-2' : 'pl-6 pr-6 py-3'} ${isCompact ? 'rounded-[16px]' : 'rounded-[20px]'} ${
          (isHovered || isSelected || showRemixSubmenu) ? 'bg-white/[0.04]' : ''
        } ${multiSelectMode ? 'cursor-pointer' : 'cursor-pointer'} ${showRemixSubmenu ? 'z-[10000]' : ''} ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleRowClick}
      onDoubleClick={handleDoubleClick}
    >
      {/* Left side - Song info */}
      <div className={`flex justify-start items-center ${isCompact ? 'flex-grow-0 flex-shrink-0' : 'self-stretch'} relative ${isCompact ? 'gap-3' : 'gap-[21px]'}`}>
        {/* Album art */}
        <div 
          className={`${isCompact ? 'w-12 h-[60px]' : 'self-stretch w-[67px]'} flex-grow-0 flex-shrink-0 relative overflow-hidden ${isCompact ? 'rounded-[6px]' : 'rounded-xl'} bg-cover bg-no-repeat bg-center`}
          style={{ backgroundImage: image ? `url(${image})` : 'linear-gradient(135deg, #363030, #252020)' }}
        >
          {/* Playing state overlay with sound wave */}
          {isPlaying && !(isHovered || showRemixSubmenu) && (
            <div className="absolute inset-0">
              <div className="absolute inset-0 bg-black/60" />
              <div className="absolute inset-0 flex items-center justify-center">
                <SoundWave />
              </div>
            </div>
          )}

          {/* Play/Pause overlay on hover or when playing and hovered */}
          <div className={`absolute inset-0 transition-opacity duration-200 ${
            (isHovered || showRemixSubmenu) ? 'opacity-100' : 'opacity-0'
          } ${multiSelectMode ? 'pointer-events-none' : ''}`}>
            <div className="absolute inset-0 bg-black/50" />
            <button 
              className="absolute inset-0 flex items-center justify-center group transition-transform duration-200 hover:scale-110"
              onClick={handleActionClick(isPlaying ? handlePauseClick : handlePlayClick)}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>
          </div>
          
          <div className="absolute bottom-0.5 right-0.5 px-2 py-1 rounded-xl bg-black/60 backdrop-blur-md">
            <p className="text-white" style={typography.duration}>
              {duration}
            </p>
          </div>
        </div>
        
        {/* Song details */}
        <div className={`flex flex-col justify-start items-start ${isCompact ? 'gap-1 flex-1' : 'gap-2 overflow-hidden flex-1 min-w-0 pr-16'}`}>
          <div className={`flex flex-col justify-start items-start ${isCompact ? 'self-stretch' : 'w-full min-w-0'} relative gap-1`}>
            <div className="flex justify-start items-center gap-1 w-full min-w-0">
              <p className={`text-left truncate overflow-hidden text-ellipsis whitespace-nowrap flex-shrink ${
                isPlaying ? 'text-[#FD429C]' : 'text-white'
              }`} style={isCompact ? {...typography.medium.title, fontWeight: 500} : typography.small.title}>
                {title}
              </p>
              <Badge text="v5" variant="primary" size="small" />
            </div>
            <p className="text-left truncate w-full overflow-hidden text-ellipsis whitespace-nowrap" 
               style={{...typography.xsmall.title, color: colors.text.inactive}}>
              {genres}
            </p>
            {/* Action buttons - inline icons with counts in compact mode */}
            {isCompact ? (
              <div className="flex gap-2 items-center">
                <div className="flex items-center gap-0.5">
                  <PlayIcon />
                  <span className="text-xs text-[#a3a3a3]">0</span>
                </div>
                <div className="flex items-center gap-0.5" onClick={handleActionClick(handleLikeClick)}>
                  <ThumbsUpIcon />
                  <span className="text-xs text-[#a3a3a3]">{isLibrary && likeCount > 0 ? formatLikeCount(likeCount) : '4'}</span>
                </div>
                <div className="flex items-center gap-0.5" onClick={handleActionClick(handleDislikeClick)}>
                  <ThumbsDownIcon />
                </div>
                {isLibrary && (
                  <div className="flex items-center gap-0.5" onClick={handleActionClick(onComment)}>
                    <CommentIcon />
                    <span className="text-xs text-[#a3a3a3]">{formatLikeCount(commentCount)}</span>
                  </div>
                )}
                <div className="flex items-center gap-0.5" onClick={handleActionClick(onShare)}>
                  <ShareIcon />
                </div>
              </div>
            ) : (
            <div className={`flex gap-2 mt-1 transition-opacity duration-200 max-w-full overflow-hidden ${
              isHovered || isLiked ? 'opacity-100' : 'opacity-30'
            }`}>
              <SmallButton
                variant={isLiked ? "primary" : "secondary"}
                onClick={handleActionClick(handleLikeClick)}
                tooltip="Like"
                leftIcon={<ThumbsUpIcon />}
                label={isLibrary && likeCount > 0 ? formatLikeCount(likeCount) : undefined}
                className={isCompact ? "scale-75" : ""}
              />
              <SmallButton
                variant={isDisliked ? "primary" : "secondary"}
                onClick={handleActionClick(handleDislikeClick)}
                tooltip="Dislike"
                leftIcon={<ThumbsDownIcon />}
                className={isCompact ? "scale-75" : ""}
              />
              {/* Only show comments in Library */}
              {isLibrary && (
                <SmallButton
                  variant={isCommented ? "primary" : "secondary"}
                  onClick={handleActionClick(onComment)}
                  tooltip="Comments"
                  leftIcon={<CommentIcon />}
                  label={formatLikeCount(commentCount)}
                  className={isCompact ? "scale-75" : ""}
                />
              )}
              <SmallButton
                variant="secondary"
                onClick={handleActionClick(onShare)}
                tooltip="Share"
                leftIcon={<ShareIcon />}
                className={isCompact ? "scale-75" : ""}
              />
              {/* Show publish button when song is liked */}
              {isLiked && (
                <SmallButton
                  variant="secondary"
                  onClick={handleActionClick(onPublish)}
                  tooltip="Publish"
                  label="Publish"
                  className={isCompact ? "scale-75" : ""}
                />
              )}
              {/* Additional actions on hover in small window state */}
              {shouldShowThirdRow && (isHovered || showRemixSubmenu) && !isPublished && (
                <>
                  <div ref={remixButtonRef} className="relative flex-shrink-0">
                    <SmallButton
                      variant="secondary"
                      onClick={handleRemixClick}
                      label="Remix/Edit"
                      rightIcon={<TriangleDownIcon />}
                      className={isCompact ? "scale-75" : ""}
                    />
                    {/* Remix Submenu - positioned relative to this button */}
                    {showRemixSubmenu && (
                      <div 
                        ref={submenuRef}
                        className="absolute top-full left-0 mt-2 z-[9999]"
                      >
                        <RemixSubmenu
                          onOpenEditor={() => handleRemixAction('openEditor')}
                          onCover={() => handleRemixAction('cover')}
                          onKaraoke={() => handleRemixAction('karaoke')}
                          onExtend={() => handleRemixAction('extend')}
                          onIntro={() => handleRemixAction('intro')}
                          onSpeed={() => handleRemixAction('speed')}
                          onReplaceSection={() => handleRemixAction('replaceSection')}
                          onReuseLyrics={() => handleRemixAction('reuseLyrics')}
                          onClose={() => setShowRemixSubmenu(false)}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Right side - Actions */}
      {isCompact ? (
        <div className="flex gap-2 items-center">
          <div className="flex justify-center items-center h-10 px-4 rounded-full bg-white/[0.04] opacity-0">
            <span className="text-xs font-medium text-[#f7f4ef]">Remix/Edit</span>
          </div>
          <div className="flex justify-center items-center w-10 h-10 rounded-full bg-white/[0.04]" onClick={handleActionClick(onMenuClick)}>
            <MoreHorizontalIcon />
          </div>
        </div>
      ) : (
      <div className="absolute right-6 top-1/2 transform -translate-y-1/2 flex justify-end items-center gap-2 min-w-[48px]">
        {/* Default state - Menu button */}
        <div className={`transition-opacity duration-200 ${
          !(isHovered || showRemixSubmenu) ? 'opacity-30' : 'opacity-0 pointer-events-none'
        }`}>
          <MediumButton
            variant="secondary"
            onClick={handleActionClick(onMenuClick)}
            tooltip="More options"
            leftIcon={<MoreHorizontalIcon />}
          />
        </div>
        
        {/* Hover state - Remix and Menu buttons (hidden when window is small) */}
        {!shouldShowThirdRow && (isHovered || showRemixSubmenu) && (
          <div className="flex gap-2 transition-opacity duration-200 absolute right-0 opacity-100">
            <div ref={remixButtonRef} className="relative">
              <MediumButton
                variant="secondary"
                onClick={handleRemixClick}
                text="Remix/Edit"
                rightIcon={<TriangleDownIcon />}
              >
              </MediumButton>
                
              {/* Remix Submenu - positioned relative to this button */}
              {showRemixSubmenu && (
                <div 
                  ref={submenuRef}
                  className="absolute top-full right-0 mt-2 z-[9999]"
                >
                  <RemixSubmenu
                    onOpenEditor={() => handleRemixAction('openEditor')}
                    onCover={() => handleRemixAction('cover')}
                    onKaraoke={() => handleRemixAction('karaoke')}
                    onExtend={() => handleRemixAction('extend')}
                    onIntro={() => handleRemixAction('intro')}
                    onSpeed={() => handleRemixAction('speed')}
                    onReplaceSection={() => handleRemixAction('replaceSection')}
                    onReuseLyrics={() => handleRemixAction('reuseLyrics')}
                    onClose={() => setShowRemixSubmenu(false)}
                  />
                </div>
              )}
            </div>
            <MediumButton
              variant="secondary"
              onClick={handleActionClick(onMenuClick)}
              tooltip="More options"
              leftIcon={<MoreHorizontalIcon />}
            />
          </div>
        )}
        
        {/* Small window state - Only menu button on hover */}
        {shouldShowThirdRow && (
          <div className={`transition-opacity duration-200 absolute right-0 ${
            (isHovered || showRemixSubmenu) ? 'opacity-100' : 'opacity-30'
          }`}>
            <MediumButton
              variant="secondary"
              onClick={handleActionClick(onMenuClick)}
              tooltip="More options"
              leftIcon={<MoreHorizontalIcon />}
            />
          </div>
        )}
      </div>
      )}

      {/* Checkbox on hover or when selected */}
      <div className={`absolute left-1.5 top-1/2 transform -translate-y-1/2 transition-opacity duration-200 ${
        (isHovered || isSelected || showRemixSubmenu) ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}>
        <input 
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          className="sr-only"
        />
        <div 
          className={`w-3 h-3 rounded border cursor-pointer flex items-center justify-center transition-all duration-200 ${
            isSelected 
              ? 'border-[#FD429C]' 
              : 'border-white/15'
          }`}
          style={{
            backgroundColor: isSelected ? colors.accent.brand : colors.bg.primary
          }}
          onClick={handleActionClick(onSelect)}
        >
          {isSelected && (
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.02129 3.29176C9.41519 2.89786 10.0512 2.90702 10.4383 3.29371C10.825 3.68075 10.8351 4.3168 10.4412 4.7107L5.84844 9.30347C5.45801 9.69374 4.82488 9.69363 4.43438 9.30347L2.2918 7.1609C1.89788 6.76697 1.90698 6.13095 2.29375 5.7439C2.68068 5.35697 3.31672 5.34725 3.71075 5.74097L5.14141 7.17164L9.02129 3.29176Z" fill="white"/>
            </svg>
          )}
        </div>
      </div>

      {/* New song indicator - Only shown when not hovered and not selected */}
      {isNew && !isHovered && !isSelected && (
        <div className="absolute left-2 top-1/2 transform -translate-y-1/2 w-2 h-2 rounded-full bg-[#FD429C]" />
      )}

      </div>
    </>
  );
};

export default Cell;